import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, CheckCircle2, Loader2, MessageSquarePlus, Sparkles, Send, FileText, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import DashboardLayout, { StatusBadge, STATUS_STAGES } from "../../components/layout/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";

const BRIEF_LABELS = {
  business_summary: "Business summary", target_audience: "Target audience", brand_tone: "Brand tone",
  primary_cta: "Primary CTA", secondary_cta: "Secondary CTA", services: "Services",
  recommended_pages: "Recommended pages", hero_headline: "Hero headline", hero_subheadline: "Hero subheadline",
  about_section: "About section", seo_keywords: "SEO keywords", meta_title: "Meta title", meta_description: "Meta description",
};

function toStr(v) { return Array.isArray(v) ? v.join("\n") : (v == null ? "" : String(v)); }

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [brief, setBrief] = useState(null);
  const [briefDraft, setBriefDraft] = useState({});
  const [briefBusy, setBriefBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [revText, setRevText] = useState("");
  const [busy, setBusy] = useState(false);
  const [revOpen, setRevOpen] = useState(false);

  const loadBrief = () => api.get(`/brief/${projectId}`).then((r) => {
    setBrief(r.data);
    if (r.data?.data) setBriefDraft(Object.fromEntries(Object.entries(r.data.data).map(([k, v]) => [k, toStr(v)])));
  }).catch(() => {});
  const loadMessages = () => api.get(`/projects/${projectId}/messages`).then((r) => setMessages(r.data)).catch(() => {});
  const load = () => {
    api.get(`/projects/${projectId}`).then((r) => setProject(r.data)).catch(() => setProject(false));
    api.get(`/revisions/project/${projectId}`).then((r) => setRevisions(r.data)).catch(() => {});
    loadBrief();
    loadMessages();
  };
  const viewed = useRef(false);
  useEffect(() => {
    load();
    if (!viewed.current) { viewed.current = true; api.post("/analytics/view", { project_id: projectId }).catch(() => {}); }
  }, [projectId]);

  if (project === false) return <DashboardLayout title="Project"><p className="text-[#948C79]">Project not found.</p></DashboardLayout>;
  if (!project) return <DashboardLayout title="Project"><Loader2 className="h-6 w-6 animate-spin text-[#C9A227]" /></DashboardLayout>;

  const currentIdx = STATUS_STAGES.indexOf(project.status);
  const progress = currentIdx >= 0 ? Math.round(((currentIdx + 1) / STATUS_STAGES.length) * 100) : 0;
  const canApprove = ["Client Review", "Revision"].includes(project.status);
  const canRevise = ["Client Review", "Revision", "Internal Review"].includes(project.status) && project.revisions_used < project.revisions_allowed;
  const files = [
    ...(project.onboarding?.logo_url ? [{ label: "Logo", url: project.onboarding.logo_url }] : []),
    ...((project.onboarding?.photos || []).map((u, i) => ({ label: `Photo ${i + 1}`, url: u }))),
  ];

  const approve = async () => {
    setBusy(true);
    try { await api.post(`/projects/${projectId}/approve`); toast.success("Website approved! 🎉"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const requestRevision = async () => {
    if (!revText.trim()) { toast.error("Describe what you'd like changed"); return; }
    setBusy(true);
    try { await api.post("/revisions", { project_id: projectId, description: revText }); toast.success("Revision requested"); setRevText(""); setRevOpen(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const sendMessage = async () => {
    if (!msgText.trim()) return;
    try { await api.post(`/projects/${projectId}/messages`, { text: msgText }); setMsgText(""); loadMessages(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const regenerateBrief = async () => {
    setBriefBusy(true);
    try { await api.post("/onboarding/generate-brief", { project_id: projectId }); toast.success("Brief generated"); loadBrief(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBriefBusy(false); }
  };
  const saveBrief = async () => {
    setBriefBusy(true);
    try { await api.patch(`/brief/${projectId}`, { data: briefDraft }); toast.success("Brief saved"); loadBrief(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBriefBusy(false); }
  };

  return (
    <DashboardLayout
      title={project.name}
      actions={<Button variant="outline" className="rounded-full" onClick={() => navigate("/portal")}><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>}
    >
      <div className="flex items-center gap-3 -mt-4 mb-6">
        <StatusBadge status={project.status} />
        <span className="text-[#7E7663] text-sm capitalize">{project.package} package · ${project.price}</span>
      </div>

      {/* Progress */}
      <Card className="p-5 mb-8" data-testid="project-progress">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-[#D2CAB2] font-medium">Project progress</span>
          <span className="text-[#C9A227] font-semibold" data-testid="progress-percent">{progress}%</span>
        </div>
        <div className="h-2.5 w-full rounded-full bg-[#1b1b1d] overflow-hidden">
          <div className="h-full bg-[#C9A227] transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Preview */}
          <Card className="overflow-hidden" data-testid="project-preview">
            <div className="p-5 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-display font-semibold text-[#F3EDDC]">Website Preview</h3>
              {project.preview_url && (
                <a href={project.preview_url} target="_blank" rel="noreferrer" className="text-sm text-[#C9A227] flex items-center gap-1">Open <ExternalLink className="h-3.5 w-3.5" /></a>
              )}
            </div>
            {project.preview_url ? (
              <div className="aspect-video bg-[#1b1b1d]"><iframe src={project.preview_url} title="preview" className="w-full h-full" /></div>
            ) : (
              <div className="p-12 text-center text-[#7E7663] text-sm">Your preview will appear here once we've started building.</div>
            )}
          </Card>

          {/* Review actions */}
          {(canApprove || canRevise) && (
            <Card className="p-6">
              <h3 className="font-display font-semibold text-[#F3EDDC]">Your review</h3>
              <p className="text-sm text-[#948C79] mt-1">Review your website and approve it, or request a revision.</p>
              <div className="mt-4 flex flex-wrap gap-3">
                {canApprove && (
                  <Button data-testid="approve-btn" onClick={approve} disabled={busy} className="rounded-full bg-green-600 hover:bg-green-700 text-white">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Approve website</>}
                  </Button>
                )}
                <Dialog open={revOpen} onOpenChange={setRevOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="revision-open-btn" variant="outline" disabled={!canRevise} className="rounded-full"><MessageSquarePlus className="h-4 w-4 mr-1" /> Request revision</Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Request a revision</DialogTitle></DialogHeader>
                    <p className="text-sm text-[#948C79]">You have {project.revisions_allowed - project.revisions_used} revision(s) remaining.</p>
                    <Textarea data-testid="revision-text" value={revText} onChange={(e) => setRevText(e.target.value)} rows={4} placeholder="Describe the changes you'd like…" />
                    <DialogFooter>
                      <Button data-testid="revision-submit" onClick={requestRevision} disabled={busy} className="rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit request"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </Card>
          )}

          {/* AI Website Brief */}
          <Card className="p-6" data-testid="brief-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold text-[#F3EDDC] flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#C9A227]" /> AI Website Brief</h3>
              <div className="flex items-center gap-2">
                {brief?.pdf_url && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${brief.pdf_url}`} target="_blank" rel="noreferrer" data-testid="brief-pdf-download" className="text-xs rounded-full border border-white/15 px-3 py-1.5 text-[#D2CAB2] hover:text-[#F3EDDC]">Download PDF</a>
                )}
                <Button data-testid="brief-regenerate" size="sm" variant="outline" onClick={regenerateBrief} disabled={briefBusy} className="rounded-full">
                  {briefBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-3.5 w-3.5 mr-1" /> Regenerate</>}
                </Button>
              </div>
            </div>
            {!brief ? (
              <p className="text-sm text-[#7E7663]">No brief yet. Complete onboarding, or click Regenerate to create one from your business details.</p>
            ) : (
              <div className="space-y-4">
                {Object.keys(briefDraft).map((k) => (
                  <div key={k} className="space-y-1.5">
                    <label className="text-xs uppercase tracking-widest text-[#7E7663]">{BRIEF_LABELS[k] || k}</label>
                    <Textarea data-testid={`brief-${k}`} value={briefDraft[k]} onChange={(e) => setBriefDraft((d) => ({ ...d, [k]: e.target.value }))} rows={toStr(briefDraft[k]).length > 80 ? 3 : 1} />
                  </div>
                ))}
                <Button data-testid="brief-save" onClick={saveBrief} disabled={briefBusy} className="rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black">
                  {briefBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save brief"}
                </Button>
              </div>
            )}
          </Card>

          {/* Messages */}
          <Card className="p-6" data-testid="messages-card">
            <h3 className="font-display font-semibold text-[#F3EDDC] mb-4">Messages</h3>
            <div className="space-y-3 max-h-72 overflow-y-auto mb-4">
              {messages.length === 0 && <p className="text-sm text-[#7E7663]">No messages yet. Say hello to your project team.</p>}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender_role === user?.role ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${m.sender_role === "admin" ? "bg-[#C9A227]/15 text-[#F3EDDC]" : "bg-[#1b1b1d] text-[#D2CAB2]"}`}>
                    <div className="text-[10px] uppercase tracking-wide text-[#7E7663] mb-0.5">{m.sender_role === "admin" ? "WebLaunch Team" : m.sender_name}</div>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Input data-testid="message-input" value={msgText} onChange={(e) => setMsgText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMessage()} placeholder="Type a message…" />
              <Button data-testid="message-send" onClick={sendMessage} className="rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black shrink-0"><Send className="h-4 w-4" /></Button>
            </div>
          </Card>

          {/* Revisions list */}
          {revisions.length > 0 && (
            <Card className="p-6" data-testid="revision-list">
              <h3 className="font-display font-semibold text-[#F3EDDC] mb-4">Revision requests</h3>
              <div className="space-y-3">
                {revisions.map((r) => (
                  <div key={r.id} className="flex items-start justify-between gap-4 border-b border-white/10 pb-3 last:border-0">
                    <p className="text-sm text-[#D2CAB2]">{r.description}</p>
                    <span className="rounded-full bg-[#1b1b1d] px-2.5 py-1 text-xs font-medium text-[#B7AF98] capitalize shrink-0">{r.status}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="p-6" data-testid="status-tracker">
            <h3 className="font-display font-semibold text-[#F3EDDC] mb-4">Project timeline</h3>
            <ol className="relative border-l border-white/10 ml-1.5 space-y-4">
              {STATUS_STAGES.map((stage, i) => {
                const active = i <= currentIdx;
                const isCurrent = i === currentIdx;
                return (
                  <li key={stage} className="ml-5">
                    <span className={`absolute -left-[7px] h-3.5 w-3.5 rounded-full border-2 ${active ? "bg-[#C9A227] border-[#C9A227]" : "bg-[#141414] border-white/20"} ${isCurrent ? "ring-4 ring-[#C9A227]/20" : ""}`} />
                    <span className={`text-sm ${isCurrent ? "font-semibold text-[#F3EDDC]" : active ? "text-[#B7AF98]" : "text-[#7E7663]"}`}>{stage}</span>
                  </li>
                );
              })}
            </ol>
          </Card>

          {/* Files */}
          <Card className="p-6" data-testid="files-card">
            <h3 className="font-display font-semibold text-[#F3EDDC] mb-4 flex items-center gap-2"><FileText className="h-4 w-4 text-[#C9A227]" /> Your files</h3>
            {files.length === 0 ? (
              <p className="text-sm text-[#7E7663]">No files uploaded yet. Add your logo and photos in onboarding.</p>
            ) : (
              <div className="space-y-2">
                {files.map((f, i) => (
                  <a key={i} href={f.url} target="_blank" rel="noreferrer" data-testid={`file-${i}`} className="flex items-center justify-between rounded-lg bg-[#1b1b1d] px-3 py-2 text-sm text-[#D2CAB2] hover:text-[#F3EDDC]">
                    {f.label} <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                ))}
              </div>
            )}
          </Card>

          {["Paid", "Onboarding"].includes(project.status) && (
            <Button asChild className="w-full rounded-full bg-[#C9A227] hover:bg-[#B0891C] text-black">
              <Link to={`/portal/onboarding/${projectId}`}>Complete onboarding</Link>
            </Button>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
